import React from 'react';
import {formatCurrency, formatDateTime, formatString} from '../../../utils/helpers';
import {Image, Tag} from 'antd';

const urlStyle: React.CSSProperties = {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const gridStyle: React.CSSProperties = {
    marginTop: 10,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px 12px',
    fontSize: 13,
};

const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
};

const titleStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.2,
};

const subStyle: React.CSSProperties = {
    fontSize: 12,
    marginTop: 4,
};

const SystemUser = (v?: string) => (v === 'promotify_system' ? 'promotify' : (v || '-'));

const cashoutTransferLabel = (status?: string) => {
    if (status === 'completed') return 'Transfer completed';
    if (status === 'failed') return 'Transfer failed';
    return 'Transfer pending';
};

const cashoutPayoutLabel = (status?: string) => {
    if (status === 'paid') return 'Bank paid';
    if (status === 'failed') return 'Bank payout failed';
    return 'Bank processing';
};

export const mobileRenderers = {
    myCampaigns: (c: any) => (
        <>
            <div style={{display: 'flex', gap: 12, alignItems: 'center'}}>
                <div style={{width: 54, height: 54, flex: '0 0 54px'}}>
                    {c?.logo?.signedUrl ? (
                        <Image
                            src={c.logo.signedUrl}
                            preview={false}
                            width={54}
                            height={54}
                            style={{objectFit: 'cover', borderRadius: 8}}
                        />
                    ) : (
                        <div style={{width: 54, height: 54, borderRadius: 8, background: '#f2f2f2'}}/>
                    )}
                </div>

                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 600, fontSize: 16, lineHeight: 1.2}}>
                        {c.title}
                    </div>
                    <div style={{fontSize: 12, marginTop: 4}}>
                        <span style={{opacity: 0.75}}>Status:</span> {formatString(c.status)}
                    </div>
                </div>
            </div>

            <div
                style={{marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13}}>
                <div><b>Click rate:</b> {formatCurrency(c.clickRate)}</div>
                <div><b>Clicks:</b> {c.clickCounter}</div>
                <div><b>Initial:</b> {formatCurrency(c.initialBudget)}</div>
                <div><b>Balance:</b> {formatCurrency(c.balance)}</div>
            </div>

            <div style={{marginTop: 10, fontSize: 13}} onClick={(e) => e.stopPropagation()}>
                <a href={c.targetURL} target="_blank" rel="noopener noreferrer" style={urlStyle}>
                    {c.targetURL}
                </a>
            </div>
        </>
    ),
    activeCampaigns: (c: any) => (
        <>
            <div style={{display: 'flex', gap: 12, alignItems: 'center'}}>
                <div style={{width: 54, height: 54, flex: '0 0 54px'}}>
                    {c?.logo?.signedUrl ? (
                        <Image
                            src={c.logo.signedUrl}
                            preview={false}
                            width={54}
                            height={54}
                            style={{objectFit: 'cover', borderRadius: 8}}
                        />
                    ) : (
                        <div style={{width: 54, height: 54, borderRadius: 8, background: '#f2f2f2'}}/>
                    )}
                </div>

                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 600, fontSize: 16, lineHeight: 1.2}}>
                        {c.title}
                    </div>
                    <div style={{fontSize: 12, marginTop: 4}}>
                        <span style={{opacity: 0.75}}>Advertiser:</span> {c?.advertiserId?.username || '-'}
                    </div>
                </div>
            </div>

            <div
                style={{marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13}}>
                <div><b>Click rate:</b> {formatCurrency(c.clickRate)}</div>
                <div><b>Clicks:</b> {c.clickCounter}</div>
                <div><b>Initial:</b> {formatCurrency(c.initialBudget)}</div>
                <div><b>Balance:</b> {formatCurrency(c.balance)}</div>
            </div>

            <div style={{marginTop: 10, fontSize: 13}} onClick={(e) => e.stopPropagation()}>
                <a href={c.targetURL} target="_blank" rel="noopener noreferrer" style={urlStyle}>
                    {c.targetURL}
                </a>
            </div>
        </>
    ),

    myCampaignLinks: (r: any) => (
        <>
            <div style={{...titleStyle, marginBottom: 4}}>
                {r?.campaignId?.title || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Click rate:</b> {formatCurrency(r?.campaignId?.clickRate)}</div>
                <div><b>Balance:</b> {formatCurrency(r?.campaignId?.balance)}</div>
                <div><b>My clicks:</b> {r?.clickCounter ?? 0}</div>
                <div><b>All clicks:</b> {r?.campaignId?.clickCounter ?? 0}</div>
            </div>

            <div style={{marginTop: 10, display: 'flex', gap: 10, alignItems: 'center'}}>
                <div style={{fontSize: 13}}>
                    <b>Status:</b>{' '}
                    {r?.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
                </div>

                <div style={{marginLeft: 'auto'}} onClick={(e) => e.stopPropagation()}>
                    <a href={r?.customUrl} target="_blank" rel="noopener noreferrer" style={urlStyle}>
                        Link here
                    </a>
                </div>
            </div>
        </>
    ),
    userCampaignLinks: (r: any) => (
        <>
            <div style={titleStyle}>{r?.campaignId?.title || '-'}</div>
            <div style={subStyle}>
                <span style={{opacity: 0.75}}>User:</span> {r?.ownerId?.username || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Click rate:</b> {formatCurrency(r?.campaignId?.clickRate)}</div>
                <div><b>Balance:</b> {formatCurrency(r?.campaignId?.balance)}</div>
                <div><b>Clicks:</b> {r?.clickCounter ?? 0}</div>
                <div><b>All clicks:</b> {r?.campaignId?.clickCounter ?? 0}</div>
            </div>

            <div style={{marginTop: 10, display: 'flex', gap: 10, alignItems: 'center'}}>
                <div style={{fontSize: 13}}>
                    <b>Status:</b>{' '}
                    {r?.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
                </div>

                <div style={{marginLeft: 'auto'}} onClick={(e) => e.stopPropagation()}>
                    <a href={r?.customUrl} target="_blank" rel="noopener noreferrer" style={urlStyle}>
                        Link here
                    </a>
                </div>
            </div>
        </>
    ),

    transactions: (t: any) => (
        <>
            <div style={headerRowStyle}>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={titleStyle}>
                        {formatString(t?.transactionType || '') || 'Transaction'}
                    </div>

                    <div style={subStyle}>
                        <span style={{opacity: 0.75}}>From:</span> {SystemUser(t?.from?.username)}{' '}
                        <span style={{opacity: 0.75, marginLeft: 8}}>To:</span> {SystemUser(t?.to?.username)}
                    </div>
                </div>

                <div style={{textAlign: 'right'}}>
                    <div style={{fontWeight: 700, fontSize: 15}}>
                        {formatCurrency(t?.amount)} {t?.currency || ''}
                    </div>
                    <div style={{marginTop: 4}}>
                        {t?.status ? (
                            <Tag color={t.status === 'completed' ? 'green' : t.status === 'failed' ? 'red' : 'gold'}>
                                {t?.transactionType === 'cash_out' ? cashoutTransferLabel(t.status) : formatString(t.status)}
                            </Tag>
                        ) : (
                            <Tag>Unknown</Tag>
                        )}
                    </div>
                </div>
            </div>

            <div style={{marginTop: 8, fontSize: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px'}}>
                <div><b>Method:</b> {formatString(t?.paymentMethod) || '-'}</div>
                <div><b>Created:</b> {formatDateTime(t?.createdAt)}</div>
                {t?.transactionType === 'cash_out' && (
                    <div>
                        <b>Bank payout:</b>{' '}
                        <Tag color={t?.payoutStatus === 'paid' ? 'green' : t?.payoutStatus === 'failed' ? 'red' : 'gold'}>
                            {cashoutPayoutLabel(t?.payoutStatus)}
                        </Tag>
                        {(!t?.payoutStatus || t.payoutStatus === 'pending') && (
                            <div style={{fontSize: 12, marginTop: 4, opacity: 0.75}}>
                                Usually arrives in 1-4 business days.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    ),

    bestAdvertisers: (u: any) => (
        <>
            <div style={titleStyle}>{u?.displayName || u?.username || '-'}</div>
            <div style={subStyle}>
                <span style={{opacity: 0.75}}>Username:</span> {u?.username || '-'}{' '}
                <span style={{opacity: 0.75, marginLeft: 8}}>Type:</span> {formatString(u?.userType) || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Active:</b> {u?.activeCampaigns ?? 0}</div>
                <div><b>Total clicks:</b> {u?.totalClicks ?? 0}</div>
                <div><b>Deposited:</b> {formatCurrency(u?.totalDeposited)}</div>
                <div><b>Spent:</b> {formatCurrency(u?.totalSpent)}</div>
            </div>

            <div style={{marginTop: 8, fontSize: 13}}>
                <b>Avg click rate:</b> {formatCurrency(u?.averageClickRate)}
            </div>
        </>
    ),
    bestPromoters: (u: any) => (
        <>
            <div style={titleStyle}>{u?.displayName || u?.username || '-'}</div>
            <div style={subStyle}>
                <span style={{opacity: 0.75}}>Username:</span> {u?.username || '-'}{' '}
                <span style={{opacity: 0.75, marginLeft: 8}}>Type:</span> {formatString(u?.userType) || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Active links:</b> {u?.activeCampaignLinks ?? 0}</div>
                <div><b>Total clicks:</b> {u?.totalClicks ?? 0}</div>
                <div><b>Earnings:</b> {formatCurrency(u?.totalMoneyGenerated)}</div>
                <div><b>Avg rate:</b> {formatCurrency(u?.averageClickRate)}</div>
            </div>
        </>
    ),
    bestPartners: (u: any) => (
        <>
            <div style={titleStyle}>{u?.displayName || u?.username || '-'}</div>
            <div style={subStyle}>
                <span style={{opacity: 0.75}}>Username:</span> {u?.username || '-'}{' '}
                <span style={{opacity: 0.75, marginLeft: 8}}>Type:</span> {formatString(u?.userType) || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Onboarded:</b> {u?.onboardedUserCount ?? 0}</div>
                <div><b>Total clicks:</b> {u?.totalClicks ?? 0}</div>
                <div><b>Onboard $:</b> {formatCurrency(u?.onboardedUserRevenue)}</div>
                <div><b>Click $:</b> {formatCurrency(u?.totalMoneyGenerated)}</div>
            </div>

            <div style={{marginTop: 8, fontSize: 13}}>
                <b>Avg click rate:</b> {formatCurrency(u?.averageClickRate)}
            </div>
        </>
    ),

    campaignPromoters: (p: any) => (
        <>
            <div style={titleStyle}>{p?.displayName || p?.username || '-'}</div>
            <div style={subStyle}>
                <span style={{opacity: 0.75}}>Username:</span> {p?.username || '-'}{' '}
                <span style={{opacity: 0.75, marginLeft: 8}}>Type:</span> {formatString(p?.userType) || '-'}
            </div>

            <div style={gridStyle}>
                <div><b>Total clicks:</b> {p?.totalClicks ?? 0}</div>
                <div><b>Click rate:</b> {formatCurrency(p?.clickRate)}</div>
                <div><b>Earnings:</b> {formatCurrency(p?.earnings)}</div>
            </div>
        </>
    ),
};
